import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  ColumnDef,
  flexRender,
} from '@tanstack/react-table'
import { toast } from 'sonner'
import { Button } from './ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog'
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react'
import { apiGetCampaignExtractions, apiPatchExtraction, BatchExtractionItem } from '@/lib/api'

type Props = {
  campaignId: string
  campaignName: string
  onBack: () => void
}

function getConfidenceBadgeClass(confidence: number): string {
  if (confidence >= 0.85) return 'bg-green-100 text-green-800'
  if (confidence >= 0.60) return 'bg-amber-100 text-amber-800'
  return 'bg-red-100 text-red-800'
}

function BatchTableSkeleton() {
  return (
    <div className="space-y-2">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="h-12 bg-gray-200 rounded animate-pulse" />
      ))}
    </div>
  )
}

export function BatchReviewView({ campaignId, campaignName, onBack }: Props) {
  const [extractions, setExtractions] = useState<BatchExtractionItem[]>([])
  const [loading, setLoading] = useState(true)
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set())
  const [rejectDialog, setRejectDialog] = useState<{ id: string } | null>(null)
  const [rejectNote, setRejectNote] = useState('')

  const loadExtractions = useCallback(() => {
    setLoading(true)
    apiGetCampaignExtractions(campaignId, 0.85)
      .then(setExtractions)
      .catch(() =>
        toast.error('Erreur lors du chargement des extractions', { duration: Infinity })
      )
      .finally(() => setLoading(false))
  }, [campaignId])

  useEffect(() => {
    loadExtractions()
  }, [loadExtractions])

  const handleAction = useCallback(
    async (id: string, action: 'accept' | 'reject', note?: string) => {
      setProcessingIds(prev => new Set([...prev, id]))
      try {
        await apiPatchExtraction(id, { action, note: note || undefined })
        setExtractions(prev => prev.filter(e => e.id !== id))
        toast.success(action === 'accept' ? 'Extraction acceptée' : 'Extraction rejetée')
      } catch {
        toast.error("Erreur lors de l'action. Veuillez réessayer.", { duration: Infinity })
      } finally {
        setProcessingIds(prev => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })
      }
    },
    []
  )

  const handleRejectConfirm = useCallback(async () => {
    if (!rejectDialog) return
    const { id } = rejectDialog
    setRejectDialog(null)
    await handleAction(id, 'reject', rejectNote)
    setRejectNote('')
  }, [rejectDialog, rejectNote, handleAction])

  const columns = useMemo<ColumnDef<BatchExtractionItem>[]>(
    () => [
      {
        id: 'supplierName',
        header: 'Fournisseur',
        cell: ({ row }) => (
          <div>
            <p className="text-sm font-medium">{row.original.supplierName}</p>
            <p className="text-xs text-muted-foreground">{row.original.supplierEmail}</p>
          </div>
        ),
      },
      {
        id: 'fieldLabel',
        header: 'Champ',
        cell: ({ row }) => (
          <span className="text-sm">{row.original.fieldLabel}</span>
        ),
      },
      {
        id: 'extractedValue',
        header: 'Valeur extraite',
        cell: ({ row }) => (
          <span className="text-sm font-mono">{row.original.extractedValue}</span>
        ),
      },
      {
        id: 'confidence',
        header: 'Confiance',
        cell: ({ row }) => {
          const pct = Math.round(row.original.confidence * 100)
          const cls = getConfidenceBadgeClass(row.original.confidence)
          return (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>
              {pct}%
            </span>
          )
        },
      },
      {
        id: 'documentUrl',
        header: 'Source',
        cell: ({ row }) => (
          <span
            className="text-xs text-muted-foreground truncate max-w-[120px] block"
            title={row.original.documentUrl}
          >
            {row.original.documentUrl}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const id = row.original.id
          const isProcessing = processingIds.has(id)
          return (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="text-green-700 border-green-200 hover:bg-green-50 h-7 px-2"
                onClick={() => void handleAction(id, 'accept')}
                disabled={isProcessing}
              >
                <CheckCircle className="h-3.5 w-3.5 mr-1" />
                Accepter
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-700 border-red-200 hover:bg-red-50 h-7 px-2"
                onClick={() => setRejectDialog({ id })}
                disabled={isProcessing}
              >
                <XCircle className="h-3.5 w-3.5 mr-1" />
                Rejeter
              </Button>
            </div>
          )
        },
      },
    ],
    [processingIds, handleAction]
  )

  const table = useReactTable({
    data: extractions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="space-y-4">
      {/* En-tête avec retour */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Retour au dashboard
        </Button>
        <div>
          <h2 className="text-lg font-semibold">Validation batch des extractions IA</h2>
          <p className="text-sm text-muted-foreground">
            {campaignName} — confiance &lt; 85%
          </p>
        </div>
      </div>

      {/* Contenu */}
      {loading ? (
        <BatchTableSkeleton />
      ) : extractions.length === 0 ? (
        <div className="rounded-lg border bg-muted/30 p-8 text-center">
          <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-3" />
          <p className="text-sm font-medium">
            Toutes les extractions ont été vérifiées pour cette campagne
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Aucune extraction IA en attente de validation.
          </p>
        </div>
      ) : (
        <div>
          <p className="text-sm text-muted-foreground mb-3">
            {extractions.length} extraction{extractions.length > 1 ? 's' : ''} en attente de
            validation
          </p>
          <div className="overflow-x-auto rounded border">
            <table className="w-full text-sm">
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id} className="bg-muted/50">
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="px-3 py-2 text-left font-medium text-muted-foreground"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr
                    key={row.id}
                    className="border-t hover:bg-muted/30 transition-colors"
                  >
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-3 py-2">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dialog de confirmation de rejet avec note optionnelle */}
      <Dialog open={rejectDialog !== null} onOpenChange={open => { if (!open) { setRejectDialog(null); setRejectNote('') } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rejeter l&apos;extraction</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Vous pouvez ajouter une note optionnelle pour expliquer le rejet.
            </p>
            <textarea
              className="w-full rounded border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              rows={3}
              placeholder="Note optionnelle (ex : valeur incohérente avec le rapport)…"
              value={rejectNote}
              onChange={e => setRejectNote(e.target.value)}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => { setRejectDialog(null); setRejectNote('') }}>
              Annuler
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => void handleRejectConfirm()}
            >
              <XCircle className="h-3.5 w-3.5 mr-1" />
              Confirmer le rejet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
